import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ArrowDown, Sparkles, Clock, FileText, Calendar, DollarSign, MapPin } from 'lucide-react';
import { AnimatedText } from '../components/AnimatedText';
import { useBookingModal } from '../contexts/BookingModalContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export const DrugAlcoholServicesPage: React.FC = () => {
    const heroRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const introRef = useRef<HTMLDivElement>(null);
    const mainRef = useRef<HTMLDivElement>(null);
    const [activeLocation, setActiveLocation] = useState<'aurora' | 'denver'>('aurora');
    const { openModal } = useBookingModal();

    useEffect(() => {
        // Ensure ScrollTrigger is registered (already done in App.tsx but good for safety if page loaded standalone-ish)
        // gsap.registerPlugin(ScrollTrigger); 

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (videoRef.current && prefersReducedMotion) {
            videoRef.current.pause();
        }

        if (prefersReducedMotion) return;

        const ctx = gsap.context(() => {
            // Hero animations (AnimatedText handles title animation)
            const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

            heroTl
                .from('.hero-divider', {
                    scaleX: 0,
                    duration: 1,
                    stagger: 0.2,
                    delay: 1.2,
                })
                .from('.hero-intro-text', {
                    opacity: 0,
                    y: 40,
                    duration: 1,
                }, '-=0.5')
                .from('.hero-scroll-indicator', {
                    opacity: 0,
                    y: -20,
                    duration: 0.6,
                }, '-=0.3');

            // Intro section animations
            gsap.from('.intro-tagline', {
                opacity: 0,
                y: 30,
                duration: 0.8,
                scrollTrigger: {
                    trigger: '.intro-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                },
            });

            gsap.from('.intro-headline', {
                opacity: 0,
                y: 50,
                duration: 1,
                scrollTrigger: {
                    trigger: '.intro-section',
                    start: 'top 75%',
                    toggleActions: 'play none none none',
                },
            });

            // Parallax effect for intro image
            gsap.to('.intro-image-parallax', {
                yPercent: -15,
                ease: 'none',
                scrollTrigger: {
                    trigger: '.intro-content-grid',
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1,
                },
            });

            gsap.from('.intro-image', {
                opacity: 0,
                x: -60,
                duration: 1.2,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.intro-content-grid',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                },
            });

            gsap.from('.intro-description', {
                opacity: 0,
                x: 60,
                duration: 1.2,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.intro-content-grid',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                },
            });

            gsap.from('.sacred-geometry', {
                opacity: 0,
                scale: 0.8,
                rotation: -90,
                duration: 1.5,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.sacred-geometry',
                    start: 'top 90%',
                    toggleActions: 'play none none none',
                },
            });

            // Continuous rotation for sacred geometry
            gsap.to('.sacred-geometry', {
                rotation: 360,
                duration: 20,
                repeat: -1,
                ease: 'none',
            });

            // Ensure base visibility first - then animate
            gsap.set(['.service-header', '.service-title', '.service-card',
                '.eval-tagline', '.eval-headline', '.eval-description',
                '.eval-step-1', '.eval-step-2', '.eval-number',
                '.dui-content', '.dui-stat',
                '.cta-content', '.cta-pill', '.stat-card',
                '.hope-title', '.hope-description'], {
                opacity: 1
            });

            // Services Overview Animations
            ScrollTrigger.batch('.service-card', {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, y: 60 },
                        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' }
                    );
                },
                start: 'top 85%',
                once: true
            });

            ScrollTrigger.batch(['.service-header', '.service-title'], {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, y: 30 },
                        { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power2.out' }
                    );
                },
                start: 'top 85%',
                once: true
            });

            // Evaluations Section
            ScrollTrigger.batch(['.eval-tagline', '.eval-headline', '.eval-description'], {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, y: 30 },
                        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power2.out' }
                    );
                },
                start: 'top 85%',
                once: true
            });

            ScrollTrigger.batch(['.eval-step-1', '.eval-step-2'], {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, y: 40, scale: 0.95 },
                        { opacity: 1, y: 0, scale: 1, duration: 1, stagger: 0.2, ease: 'power3.out' }
                    );
                },
                start: 'top 80%',
                once: true
            });

            ScrollTrigger.batch('.eval-number', {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, scale: 0.5 },
                        { opacity: 1, scale: 1, duration: 1, stagger: 0.2, ease: 'back.out(1.7)' }
                    );
                },
                start: 'top 80%',
                once: true
            });

            // DUI Section
            ScrollTrigger.batch(['.dui-content', '.dui-stat'], {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, y: 40, scale: 0.95 },
                        { opacity: 1, y: 0, scale: 1, duration: 0.8, stagger: 0.1, ease: 'power2.out' }
                    );
                },
                start: 'top 85%',
                once: true
            });

            // CTA Section
            ScrollTrigger.batch(['.cta-content', '.cta-pill', '.stat-card'], {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, y: 40 },
                        { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power2.out' }
                    );
                },
                start: 'top 85%',
                once: true
            });

            // Hope Section
            ScrollTrigger.batch(['.hope-title', '.hope-description'], {
                onEnter: (elements) => {
                    gsap.fromTo(elements,
                        { opacity: 0, y: 40 },
                        { opacity: 1, y: 0, duration: 1, stagger: 0.2, ease: 'power3.out' }
                    );
                },
                start: 'top 75%',
                once: true
            });

        }, mainRef);

        return () => ctx.revert();
    }, []);

    return (
        <div ref={mainRef} className="drug-alcohol-page bg-white">
            {/* Hero Section - Redesigned to match inspiration */}
            <section
                ref={heroRef}
                className="relative min-h-screen overflow-hidden"
                aria-label="Drug and Alcohol Services Hero"
            >
                {/* Video Background - Fixed path with double dots to match actual filename */}
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster="/images/HeroFallback-min.png"
                    aria-hidden="true"
                >
                    <source src="/videos/hero-therapy.mp4" type="video/mp4" />
                </video>

                {/* Dark Gradient Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.5) 100%)',
                    }}
                    aria-hidden="true"
                />

                {/* Hero Content - Full height flex layout */}
                <div className="relative z-10 w-full h-screen flex flex-col px-6 sm:px-8 lg:px-12">
                    {/* Top Section - Title Area (spans ~55-60% of width) */}
                    <div className="flex-1 flex flex-col justify-center pt-24">
                        {/* Main Title - Large, at top, spanning 55-60% */}
                        <div className="max-w-[60%]">
                            <div className="text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-serif text-white leading-[0.95]">
                                <AnimatedText
                                    text="Drug and Alcohol"
                                    as="span"
                                    className="block"
                                    delay={200}
                                    letterDelay={35}
                                    duration={600}
                                />
                                <AnimatedText
                                    text="Treatment Services"
                                    as="span"
                                    className="block"
                                    delay={700}
                                    letterDelay={35}
                                    duration={600}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section - Two columns with divider lines */}
                    <div className="pb-12 lg:pb-16">
                        {/* Thin Divider Line - Full width */}
                        <div className="hero-divider w-full h-px bg-white/30 mb-8 origin-left"></div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
                            {/* Left Column - Empty or could have other content */}
                            <div className="hidden lg:block">
                                {/* Removed per user request - no Conscious Nourishment offer */}
                            </div>

                            {/* Right Column - Introduction */}
                            <div className="flex flex-col">
                                {/* Thin Divider Line - Right section */}
                                <div className="hero-divider w-full h-px bg-white/30 mb-6 origin-left lg:hidden"></div>

                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 hero-intro-text">
                                            Get Help Now
                                        </p>
                                        <p
                                            className="hero-intro-text text-white/80 leading-relaxed"
                                            style={{
                                                fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                                                maxWidth: '380px',
                                            }}
                                        >
                                            We'll help you on your path to 100% Sobriety, allowing you to enjoy life to the fullest and pursue your goals.
                                        </p>
                                    </div>

                                    {/* Scroll Down Indicator - Right side */}
                                    <button
                                        onClick={() => {
                                            const introSection = document.querySelector('.intro-section');
                                            if (introSection) {
                                                introSection.scrollIntoView({ behavior: 'smooth' });
                                            }
                                        }}
                                        className="hero-scroll-indicator flex flex-col items-center ml-8 group cursor-pointer"
                                        aria-label="Scroll to next section"
                                    >
                                        <ArrowDown className="w-5 h-5 text-[#d68800] group-hover:text-white animate-bounce transition-colors duration-300" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Intro Section - Dark Background */}
            <section
                ref={introRef}
                className="intro-section py-24 lg:py-32"
                style={{ backgroundColor: '#293039' }}
            >
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    {/* Tagline */}
                    <div className="intro-tagline flex items-center justify-center gap-3 mb-16">
                        <Sparkles className="w-5 h-5 text-[#d68800]" />
                        <p
                            className="text-xs uppercase tracking-[0.4em] font-semibold"
                            style={{ color: '#e9dfd1' }}
                        >
                            You Deserve Freedom
                        </p>
                    </div>

                    {/* Main Headline */}
                    <h2
                        className="intro-headline text-center font-bold leading-[1.1] mb-20 mx-auto"
                        style={{
                            fontSize: 'clamp(2rem, 5vw, 4rem)',
                            maxWidth: '1000px',
                            color: '#fff',
                        }}
                    >
                        We specialize in treating co-occurring disorders, addressing both <span className="text-gray-400">mental health</span> and <span className="text-gray-400">substance misuse.</span>
                    </h2>

                    {/* Content Grid */}
                    <div className="intro-content-grid grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Image with Parallax and Hover Effects */}
                        <div className="intro-image relative overflow-hidden">
                            <div
                                className="intro-image-parallax relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] max-w-md mx-auto lg:mx-0 group cursor-pointer"
                            >
                                <img
                                    src="/images/DrugAbuseHelp (6).jpeg"
                                    alt="Person in peaceful pose representing recovery"
                                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                {/* Hover overlay with subtle brightness */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="intro-description space-y-8">
                            <p
                                className="leading-relaxed"
                                style={{
                                    fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                                    color: '#e9dfd1',
                                    opacity: 0.85,
                                }}
                            >
                                If you suffer from mood disorders, anxiety, stress or trauma, these conditions can create challenges for coping with everyday life.
                            </p>
                            <p
                                className="leading-relaxed"
                                style={{
                                    fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                                    color: '#e9dfd1',
                                    opacity: 0.85,
                                }}
                            >
                                Our integrated approach addresses both substance use and underlying mental health conditions, providing you with comprehensive support on your journey to recovery.
                            </p>

                            {/* Flower Icon with Continuous Rotation */}
                            <div className="pt-8 flex justify-end">
                                <div
                                    className="sacred-geometry w-24 h-24 lg:w-32 lg:h-32 opacity-70 hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                                    style={{
                                        animation: 'slowRotate 20s linear infinite',
                                    }}
                                >
                                    <img
                                        src="/images/Rotating Flower.png"
                                        alt="Decorative flower"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 2: Services Overview - Staggered Cards */}
            <section className="services-overview py-24 lg:py-32 bg-white overflow-hidden">
                <div className="w-full px-6 sm:px-8 lg:px-12">
                    {/* Section Header - Left aligned */}
                    <div className="mb-16 lg:mb-24">
                        <p className="text-xs uppercase tracking-[0.3em] text-mscg-orange mb-4 service-header">
                            What We Offer
                        </p>
                        <h2
                            className="text-4xl md:text-5xl lg:text-6xl font-serif text-mscg-dark-gray leading-tight service-title"
                            style={{ maxWidth: '600px' }}
                        >
                            Comprehensive Treatment Services
                        </h2>
                    </div>

                    {/* Staggered Service Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                        {/* Card 1 - Evaluations */}
                        <div
                            className="service-card group relative bg-gradient-to-br from-[#293039] to-[#1a1f25] rounded-3xl p-8 lg:p-10 transform hover:-translate-y-2 transition-all duration-500 cursor-pointer"
                            onClick={() => {
                                const section = document.querySelector('.evaluations-section');
                                if (section) section.scrollIntoView({ behavior: 'smooth' });
                            }}
                        >
                            <div className="mb-8">
                                <span className="text-7xl lg:text-8xl font-serif text-white/10 absolute top-6 right-6">01</span>
                                <div className="w-14 h-14 rounded-2xl bg-mscg-orange/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <svg className="w-7 h-7 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-2xl font-serif text-white mb-4">Substance Use Evaluations</h3>
                            <p className="text-white/60 leading-relaxed mb-6">
                                Comprehensive 2-part assessment to understand your unique situation and create a personalized treatment plan.
                            </p>
                            <div className="flex items-center gap-2 text-mscg-orange group-hover:gap-4 transition-all duration-300">
                                <span className="text-sm font-medium">Learn more</span>
                                <ArrowDown className="w-4 h-4" />
                            </div>
                        </div>

                        {/* Card 2 - DUI Classes - Offset */}
                        <div
                            className="service-card group relative bg-gradient-to-br from-[#293039] to-[#1a1f25] rounded-3xl p-8 lg:p-10 transform hover:-translate-y-2 transition-all duration-500 cursor-pointer lg:translate-y-12"
                            onClick={() => {
                                const section = document.querySelector('.dui-section');
                                if (section) section.scrollIntoView({ behavior: 'smooth' });
                            }}
                        >
                            <div className="mb-8">
                                <span className="text-7xl lg:text-8xl font-serif text-white/10 absolute top-6 right-6">02</span>
                                <div className="w-14 h-14 rounded-2xl bg-mscg-orange/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <svg className="w-7 h-7 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-2xl font-serif text-white mb-4">DUI Education Classes</h3>
                            <p className="text-white/60 leading-relaxed mb-6">
                                Level I & Level II education programs that meet Colorado OBH requirements for DUI sentencing.
                            </p>
                            <div className="flex items-center gap-2 text-mscg-orange group-hover:gap-4 transition-all duration-300">
                                <span className="text-sm font-medium">Learn more</span>
                                <ArrowDown className="w-4 h-4" />
                            </div>
                        </div>

                        {/* Card 3 - Therapy */}
                        <div
                            className="service-card group relative bg-gradient-to-br from-[#293039] to-[#1a1f25] rounded-3xl p-8 lg:p-10 transform hover:-translate-y-2 transition-all duration-500 cursor-pointer"
                            onClick={() => {
                                const section = document.querySelector('.dui-section');
                                if (section) {
                                    section.scrollIntoView({ behavior: 'smooth' });
                                    // Wait for scroll then click the Level II Therapy tab
                                    setTimeout(() => {
                                        const therapyTab = document.querySelector('[data-tab="level2therapy"]') as HTMLButtonElement;
                                        if (therapyTab) therapyTab.click();
                                    }, 800);
                                }
                            }}
                        >
                            <div className="mb-8">
                                <span className="text-7xl lg:text-8xl font-serif text-white/10 absolute top-6 right-6">03</span>
                                <div className="w-14 h-14 rounded-2xl bg-mscg-orange/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <svg className="w-7 h-7 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-2xl font-serif text-white mb-4">Level II Therapy</h3>
                            <p className="text-white/60 leading-relaxed mb-6">
                                Extended group therapy sessions for deeper healing and sustainable recovery support.
                            </p>
                            <div className="flex items-center gap-2 text-mscg-orange group-hover:gap-4 transition-all duration-300">
                                <span className="text-sm font-medium">Learn more</span>
                                <ArrowDown className="w-4 h-4" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 3: Evaluations Process - Visual Steps */}
            <section className="evaluations-section py-24 lg:py-32 bg-[#f8f7f5]">
                <div className="w-full px-6 sm:px-8 lg:px-12">
                    {/* Section Header - Left Aligned */}
                    <div className="mb-20">
                        <p className="text-xs uppercase tracking-[0.3em] text-mscg-orange mb-4 eval-tagline">
                            The Process
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-mscg-dark-gray mb-6 eval-headline">
                            Substance Use Evaluation
                        </h2>
                        <p className="text-lg text-neutral-500 max-w-2xl eval-description">
                            A comprehensive 2-part process designed to understand your needs and create a personalized path forward.
                        </p>
                    </div>

                    {/* Process Steps - Full Width */}
                    <div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 relative">
                            {/* Connecting Line - Desktop */}
                            <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-px">
                                <div className="eval-connector bg-mscg-orange/30 h-full w-0 origin-left"></div>
                            </div>

                            {/* Step 1 */}
                            <div className="eval-step-1 relative bg-white rounded-3xl p-10 lg:p-12 shadow-xl shadow-black/5">
                                <div className="absolute -top-6 left-10">
                                    <span className="eval-number text-8xl lg:text-9xl font-serif text-mscg-orange/20">01</span>
                                </div>
                                <div className="pt-16">
                                    <span className="inline-block px-4 py-1.5 bg-mscg-orange/10 text-mscg-orange text-xs uppercase tracking-wider rounded-full mb-6">
                                        Virtual Assessment
                                    </span>
                                    <h3 className="text-2xl lg:text-3xl font-serif text-mscg-dark-gray mb-4">
                                        Initial Evaluation
                                    </h3>
                                    <p className="text-neutral-500 leading-relaxed mb-6">
                                        Complete a comprehensive online assessment from the comfort of your home. This confidential evaluation helps us understand your history and current situation.
                                    </p>
                                    <div className="bg-[#f8f7f5] rounded-xl p-4 mb-6">
                                        <p className="text-sm text-neutral-600 mb-2"><strong>Fee:</strong> $300 flat rate (self-pay unless covered by insurance)</p>
                                        <p className="text-sm text-neutral-500"><strong>Payment:</strong> $150 before first meeting • $150 before final report</p>
                                    </div>
                                    <div className="flex flex-wrap gap-4">
                                        <div className="flex items-center gap-2 bg-[#f8f7f5] px-4 py-2 rounded-full">
                                            <span className="text-2xl font-serif text-mscg-dark-gray">1-1.5</span>
                                            <span className="text-xs text-neutral-400 uppercase">Hours</span>
                                        </div>
                                        <div className="flex items-center gap-2 bg-[#f8f7f5] px-4 py-2 rounded-full">
                                            <span className="text-2xl font-serif text-mscg-dark-gray">$300</span>
                                            <span className="text-xs text-neutral-400 uppercase">Flat Fee</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Step 2 - With Background Image */}
                            <div className="eval-step-2 relative rounded-3xl p-10 lg:p-12 shadow-xl shadow-black/5 lg:translate-y-12 overflow-hidden min-h-[400px]">
                                {/* Background Image */}
                                <div className="absolute inset-0">
                                    <img
                                        src="/images/IndividualSession (1).jpeg"
                                        alt="Counseling session"
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/60"></div>
                                </div>
                                <div className="relative z-10">
                                    <div className="absolute -top-6 left-0">
                                        <span className="eval-number text-8xl lg:text-9xl font-serif text-white/20">02</span>
                                    </div>
                                    <div className="pt-16">
                                        <span className="inline-block px-4 py-1.5 bg-mscg-orange text-white text-xs uppercase tracking-wider rounded-full mb-6">
                                            Review Session
                                        </span>
                                        <h3 className="text-2xl lg:text-3xl font-serif text-white mb-4">
                                            Personalized Consultation
                                        </h3>
                                        <p className="text-white/80 leading-relaxed mb-6">
                                            Meet with a licensed counselor to review your assessment results and discuss your personalized treatment recommendations.
                                        </p>
                                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
                                            <p className="text-sm text-white/90"><strong>Format:</strong> First meeting virtual, second meeting virtual or in-person</p>
                                        </div>
                                        <div className="flex flex-wrap gap-4">
                                            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                                                <svg className="w-5 h-5 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                                <span className="text-sm text-white/90">Telehealth Available</span>
                                            </div>
                                            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                                                <svg className="w-5 h-5 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <span className="text-sm text-white/90">In-Person Option</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section: There's Hope - Video Background Break */}
            <section className="hope-section relative h-[100vh] min-h-[500px] flex items-center overflow-hidden">
                <video
                    className="absolute top-0 left-0 w-full h-[140%] object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster="/images/Depressed.jpg"
                >
                    <source src="/videos/FreedomWalk.mp4" type="video/mp4" />
                    <img
                        src="/images/Depressed.jpg"
                        alt="Hope and recovery"
                        className="absolute top-0 left-0 w-full h-[140%] object-cover"
                    />
                </video>
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="relative z-10 w-full px-6 sm:px-8 lg:px-12">
                    <div className="max-w-4xl">
                        <h2 className="hope-title text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            A <span className='text-yellow-500'>Clear</span> Path Forward
                        </h2>
                        <p className="hope-description text-white/80 leading-relaxed text-lg mb-8 max-w-xl">
                            Get compassionate, judgment-free support to help you meet court obligations, complete evaluations, and begin meaningful change.
                        </p>
                        <a
                            href="#/contact"
                            className="inline-flex items-center gap-3 bg-white text-black pl-8 pr-2 py-2 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group"
                        >
                            Start Your Journey
                            <div className="w-10 h-10 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                                <ArrowUpRight className="w-5 h-5" />
                            </div>
                        </a>
                    </div>
                </div>
            </section>

            {/* Section 4: DUI Education Classes - Bento Grid with Tabs */}
            <DUIEducationSection />

            {/* Section 5: CTA - Ready For An Evaluation */}
            <section className="cta-section py-24 lg:py-32 bg-[#293039] relative overflow-hidden">
                {/* Background Decoration */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-mscg-orange rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-mscg-orange rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
                </div>

                <div className="relative z-10 w-full px-6 sm:px-8 lg:px-12">
                    <div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                            {/* Left - Content */}
                            <div className="cta-content">
                                <p className="text-xs uppercase tracking-[0.3em] text-mscg-orange mb-6">
                                    Take The First Step
                                </p>
                                <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
                                    Ready For An<br />Evaluation?
                                </h2>
                                <p className="text-lg text-white/60 mb-10 max-w-md leading-relaxed">
                                    Start your journey to recovery today with a confidential, no-pressure evaluation from our experienced team.
                                </p>

                                {/* Feature Pills */}
                                <div className="flex flex-wrap gap-3 mb-10">
                                    <span className="cta-pill inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm">
                                        <Sparkles className="w-4 h-4 text-mscg-orange" />
                                        Online Assessments
                                    </span>
                                    <span className="cta-pill inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm">
                                        <svg className="w-4 h-4 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Telehealth Available
                                    </span>
                                    <span className="cta-pill inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm">
                                        <svg className="w-4 h-4 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Same-Week Appointments
                                    </span>
                                </div>

                                {/* CTA Button */}
                                <button
                                    onClick={openModal}
                                    className="cta-button inline-flex items-center gap-3 bg-white text-black pl-8 pr-2 py-3 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group"
                                >
                                    Schedule Your Evaluation
                                    <div className="w-12 h-12 rounded-full bg-mscg-orange text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                                        <ArrowUpRight className="w-6 h-6" />
                                    </div>
                                </button>
                            </div>

                            {/* Right - Stats/Trust Signals */}
                            <div className="cta-stats grid grid-cols-2 gap-6">
                                <div className="stat-card bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 lg:p-8 transform hover:scale-105 transition-transform duration-300">
                                    <span className="text-5xl lg:text-6xl font-serif text-white mb-2 block">23+</span>
                                    <span className="text-white/50 text-sm">Years of Experience</span>
                                </div>
                                <div className="stat-card bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 lg:p-8 transform hover:scale-105 transition-transform duration-300 translate-y-6">
                                    <span className="text-5xl lg:text-6xl font-serif text-white mb-2 block">100%</span>
                                    <span className="text-white/50 text-sm">Confidential</span>
                                </div>
                                {/* OBH Card - With Background Image */}
                                <div className="stat-card relative rounded-2xl p-6 lg:p-8 transform hover:scale-105 transition-transform duration-300 overflow-hidden">
                                    <div className="absolute inset-0">
                                        <img
                                            src="/images/CounselingSession (3).jpeg"
                                            alt="Counseling session"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/50"></div>
                                    </div>
                                    <div className="relative z-10">
                                        <span className="text-5xl lg:text-6xl font-serif text-white mb-2 block">OBH</span>
                                        <span className="text-white/80 text-sm">Approved Programs</span>
                                    </div>
                                </div>
                                <div className="stat-card bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 lg:p-8 transform hover:scale-105 transition-transform duration-300 translate-y-6">
                                    <span className="text-5xl lg:text-6xl font-serif text-mscg-orange mb-2 block">✓</span>
                                    <span className="text-white/50 text-sm">Insurance Accepted</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section: Locations & Availability */}
            <section className="locations-section py-20 lg:py-28 bg-[#f8f7f5]">
                <div className="w-full px-6 sm:px-8 lg:px-12">
                    {/* Section Header */}
                    <div className="mb-12">
                        <p className="text-xs uppercase tracking-[0.3em] text-mscg-orange mb-4">
                            Visit Us
                        </p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-mscg-dark-gray leading-tight mb-4">
                            Our Locations
                        </h2>
                        <p className="text-lg text-neutral-500 max-w-2xl">
                            All services are available at both our Aurora and Denver locations. We offer hybrid and virtual options to fit your schedule.
                        </p>
                    </div>

                    {/* Locations Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        {/* Left - Location Details */}
                        <div>
                            {/* Location Toggle Tabs */}
                            <div className="flex rounded-full bg-white p-1 mb-8 shadow-md max-w-md">
                                <button
                                    onClick={() => setActiveLocation('aurora')}
                                    className={`flex-1 py-3 px-6 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 ${activeLocation === 'aurora' ? 'bg-mscg-orange text-white shadow-md' : 'text-neutral-600 hover:text-neutral-900'}`}
                                >
                                    Aurora Office
                                </button>
                                <button
                                    onClick={() => setActiveLocation('denver')}
                                    className={`flex-1 py-3 px-6 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 ${activeLocation === 'denver' ? 'bg-mscg-orange text-white shadow-md' : 'text-neutral-600 hover:text-neutral-900'}`}
                                >
                                    Denver Office
                                </button>
                            </div>

                            {/* Location Info Card */}
                            <div className="bg-white rounded-3xl p-8 shadow-lg">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-2xl bg-mscg-orange/10 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="w-6 h-6 text-mscg-orange" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-serif text-mscg-dark-gray mb-1">
                                            {activeLocation === 'aurora' ? 'Aurora Office' : 'Denver Office'}
                                        </h3>
                                        <p className="text-neutral-500">
                                            {activeLocation === 'aurora'
                                                ? '12510 E. Iliff Ave. Suite 230, Aurora, CO 80014'
                                                : '12000 E 47th Ave #113, Denver, CO 80239'}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 text-neutral-600">
                                        <Clock className="w-5 h-5 text-mscg-orange" />
                                        <span>Monday–Friday 8:00am–6:00pm | By Appointment Only</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-neutral-600">
                                        <svg className="w-5 h-5 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <a href="tel:303-323-8730" className="hover:text-mscg-orange transition-colors">303-323-8730</a>
                                    </div>
                                    <div className="flex items-center gap-3 text-neutral-600">
                                        <svg className="w-5 h-5 text-mscg-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <span>Virtual sessions available</span>
                                    </div>
                                </div>

                                <div className="mt-8 pt-6 border-t border-neutral-100">
                                    <a
                                        href={activeLocation === 'aurora'
                                            ? 'https://maps.google.com/?q=12510+E.+Iliff+Ave.+Suite+230+Aurora+CO+80014'
                                            : 'https://maps.google.com/?q=12000+E+47th+Ave+113+Denver+CO+80239'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-mscg-orange hover:gap-4 transition-all duration-300 font-medium"
                                    >
                                        Get Directions
                                        <ArrowUpRight className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Right - Map */}
                        <div className="rounded-3xl overflow-hidden shadow-lg h-[400px] lg:h-[500px]">
                            {activeLocation === 'aurora' ? (
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3073.5!2d-104.859422!3d39.673865!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDQwJzI2LjAiTiAxMDTCsDUxJzM0LjAiVw!5e0!3m2!1sen!2sus!4v1"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    title="Aurora Office Location"
                                />
                            ) : (
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3066.5!2d-104.850366!3d39.781988!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876c7b33ee33ef75%3A0x60b5a84d56bbda97!2s12000%20E%2047th%20Ave%20%23113%2C%20Denver%2C%20CO%2080239!5e0!3m2!1sen!2sus!4v1"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    title="Denver Office Location"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Animation keyframes */}
            <style>{`
                @keyframes slowRotate {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    );
};

// DUI Education Section Component - With Interactive Tabs
const DUIEducationSection: React.FC = () => {
    const [activeTab, setActiveTab] = React.useState<'level1' | 'level2ed' | 'level2therapy'>('level1');

    const tabContent = {
        level1: {
            title: 'Level I DUI Education',
            subtitle: '12-Hour Program • 3-6 Weeks',
            hours: '12',
            sessions: '3-6',
            duration: '3-6 Weeks',
            cost: '$275',
            durationImage: '/images/CounselingSession (2).jpeg',
            description: 'Our Level I program provides essential education about alcohol and its effects, helping you understand the risks and develop strategies for responsible behavior.',
            requirements: [
                '$275 flat fee (non-refundable)',
                'No payment plans available',
                'Not covered by insurance',
                'Curriculum materials fee applies',
                'Must complete within court-ordered timeframe',
            ]
        },
        level2ed: {
            title: 'Level II DUI Education',
            subtitle: '24-Hour Program • 12 Weeks',
            hours: '24',
            sessions: '12',
            duration: '12 Weeks',
            cost: '$30/class',
            durationImage: '/images/TherapyClass (2).jpeg',
            description: 'Extended 24-hour education program required before starting Level II Therapy. Includes deeper exploration of substance use patterns and behavioral change strategies.',
            requirements: [
                '$30 per class (12 classes total)',
                'Not covered by insurance',
                'Must complete before Level II Therapy',
                'Regular attendance mandatory',
            ]
        },
        level2therapy: {
            title: 'Level II Therapy – Track System',
            subtitle: 'Insurance may apply • Tracks A through D',
            hours: '42-86',
            sessions: '21-43',
            duration: '21-43 Weeks',
            cost: '$30/session',
            durationImage: '/images/TherapyClass (3).jpeg',
            description: 'Comprehensive group therapy program assigned by track based on state guidelines. Medicaid and commercial insurance may cover sessions. Out-of-pocket fees include $75 intake, $30 per group session, and $30 materials fee.',
            requirements: [
                'Track A: 42 hrs (~$735 out-of-pocket)',
                'Track B: 52 hrs (~$885 out-of-pocket)',
                'Track C: 68 hrs (~$1,125 out-of-pocket)',
                'Track D: 86 hrs (~$1,395 out-of-pocket)',
                'Prepay discount: $27/group session',
            ]
        }
    };

    const content = tabContent[activeTab];

    return (
        <section className="dui-section py-24 lg:py-32 bg-white">
            <div className="w-full px-6 sm:px-8 lg:px-12">
                {/* Section Header */}
                <div className="mb-16">
                    <p className="text-xs uppercase tracking-[0.3em] text-mscg-orange mb-4">
                        Colorado OBH Approved
                    </p>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-mscg-dark-gray leading-tight">
                        DUI Education Classes
                    </h2>
                </div>

                {/* Tab Navigation */}
                <div className="flex flex-wrap gap-3 mb-12">
                    {(['level1', 'level2ed', 'level2therapy'] as const).map((tab) => (
                        <button
                            key={tab}
                            data-tab={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === tab
                                ? 'bg-mscg-dark-gray text-white'
                                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                }`}
                        >
                            {tab === 'level1' ? 'Level I Education' : tab === 'level2ed' ? 'Level II Education' : 'Level II Therapy'}
                        </button>
                    ))}
                </div>

                {/* Bento Grid Content */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Main Content Card */}
                    <div className="lg:col-span-7 bg-[#f8f7f5] rounded-3xl p-8 lg:p-10 dui-content">
                        <span className="inline-block px-4 py-1.5 bg-mscg-orange/10 text-mscg-orange text-xs uppercase tracking-wider rounded-full mb-6">
                            {content.subtitle}
                        </span>
                        <h3 className="text-3xl lg:text-4xl font-serif text-mscg-dark-gray mb-6">
                            {content.title}
                        </h3>
                        <p className="text-neutral-500 leading-relaxed mb-8 text-lg">
                            {content.description}
                        </p>

                        {/* Requirements */}
                        <div>
                            <p className="text-xs uppercase tracking-wider text-neutral-400 mb-4">Requirements</p>
                            <ul className="space-y-3">
                                {content.requirements.map((req, i) => (
                                    <li key={i} className="flex items-start gap-3 text-neutral-600">
                                        <svg className="w-5 h-5 text-mscg-orange flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span>{req}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Stats Cards Column */}
                    <div className="lg:col-span-5 grid grid-cols-2 gap-6">
                        {/* Hours */}
                        <div className="bg-mscg-dark-gray rounded-3xl p-6 lg:p-8 flex flex-col justify-between dui-stat min-h-[180px]">
                            <div className="flex items-center justify-between">
                                <span className="text-white/40 text-xs uppercase tracking-wider">Total Hours</span>
                                <Clock className="w-5 h-5 text-white/30" />
                            </div>
                            <div>
                                <span className="text-3xl sm:text-5xl lg:text-6xl font-serif text-white block">{content.hours}</span>
                                <span className="text-white/50 text-sm">hours</span>
                            </div>
                        </div>

                        {/* Sessions */}
                        <div className="bg-mscg-orange rounded-3xl p-6 lg:p-8 flex flex-col justify-between dui-stat min-h-[180px]">
                            <div className="flex items-center justify-between">
                                <span className="text-white/70 text-xs uppercase tracking-wider">Sessions</span>
                                <FileText className="w-5 h-5 text-white/50" />
                            </div>
                            <div>
                                <span className="text-3xl sm:text-5xl lg:text-6xl font-serif text-white block">{content.sessions}</span>
                                <span className="text-white/70 text-sm">sessions</span>
                            </div>
                        </div>

                        {/* Duration - With Background Image */}
                        <div className="relative rounded-3xl p-6 lg:p-8 flex flex-col justify-between dui-stat min-h-[180px] overflow-hidden">
                            <div className="absolute inset-0">
                                <img
                                    src={content.durationImage}
                                    alt="Program session"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/50"></div>
                            </div>
                            <div className="relative z-10 flex items-center justify-between">
                                <span className="text-white/70 text-xs uppercase tracking-wider">Duration</span>
                                <Calendar className="w-5 h-5 text-white/50" />
                            </div>
                            <div className="relative z-10">
                                <span className="text-2xl lg:text-3xl font-serif text-white block">{content.duration}</span>
                            </div>
                        </div>

                        {/* Cost */}
                        <div className="bg-[#f8f7f5] rounded-3xl p-6 lg:p-8 flex flex-col justify-between dui-stat min-h-[180px]">
                            <div className="flex items-center justify-between">
                                <span className="text-neutral-400 text-xs uppercase tracking-wider">Program Cost</span>
                                <DollarSign className="w-5 h-5 text-neutral-300" />
                            </div>
                            <div>
                                <span className="text-2xl lg:text-3xl font-serif text-mscg-dark-gray block">{content.cost}</span>
                                <span className="text-neutral-400 text-xs">+ materials</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DrugAlcoholServicesPage;


